import { GettingStartedGuide } from '../../app/content/components/GettingStartedGuide';
import { GuidePanelFactory } from '../../app/content/welcomeScreen/GuidePanelFactory';
import { DeprecatedEventWrapper } from '../../common/analyticsEvents/DeprecatedEventWrapper';
import { AngularContextFactory } from '../factories/AngularContextFactory';
import { isCurrentUrlViz } from '../helpers/UrlHelpers';
import { Module } from '../Module';
import {
  GetGettingStartedNotificationsRequest,
  IGetGettingStartedNotificationsParams,
  IGetGettingStartedNotificationsResult,
  SetSuppressGettingStartedNotification,
} from '../ServerApi/requests/GetGettingStartedNotificationsRequest';
import { ContentActions } from './ContentActions';
import { DeprecatedAnalyticsService } from './DeprecatedAnalyticsService';
import { IModalInstance, ModalService, ModalSize } from './ModalService';
import { ServerService } from './ServerService';
import { WindowLocationService } from './WindowLocationService';

export interface IGettingStartedNotificationsScope extends ng.IScope {
  guideProps: GettingStartedGuide.Props;
  modal: IModalInstance;
  notificationsUrl: any;
  dialog: {
    showNotificationsOnLogIn: boolean;
  };
}

export class GettingStartedNotifications {
  private static CloseModalMessage = 'tableau.closeModal';

  private notificationsEnabled: boolean;
  private notificationsShown: boolean; // Track whether we've already shown the notifications
  public static $inject = [
    '$q',
    '$translate',
    'ServerService',
    'WindowLocationService',
    'AngularContextFactory',
    'DeprecatedAnalyticsService',
    'ContentActions'];

  private deprecatedAnalyticsEvents: DeprecatedEventWrapper;
  private guidePanelFactory: GuidePanelFactory;

  constructor(private $q: ng.IQService,
              private $translate: ng.translate.ITranslateService,
              private ServerService: ServerService,
              WindowLocationService: WindowLocationService,
              private angularContextFactory: AngularContextFactory,
              DeprecatedAnalyticsService: DeprecatedAnalyticsService,
              ContentActions: ContentActions
  ) {
    this.notificationsShown = false;
    this.notificationsEnabled = false;
    this.deprecatedAnalyticsEvents = DeprecatedAnalyticsService.EventFactory('');
    this.guidePanelFactory = new GuidePanelFactory(this.deprecatedAnalyticsEvents, $translate.instant,
      ServerService, WindowLocationService, ContentActions);
  }

  public enableNotifications(): void {
    this.notificationsEnabled = true;
  }

  public forceShowNotifications(): ng.IPromise<any> {
    return this.$q.resolve(); // Prevent the modal from ever being created. (TFS 872022)
  }

  // this isn't even getting used anywhere but User Journey will clean this up/delete it TFS 872022
  public showNotificationsIfEnabled(): ng.IPromise<any> {
    // Don't show notifications if we're going to a viz state
    if (!this.notificationsEnabled || this.notificationsShown || isCurrentUrlViz()) {
      return this.$q.when(null);
    }
    return this.showNotifications({ checkUserPrefs: true })
      .finally(() => this.notificationsShown = true); // Suppress in case of future remounting
  }

  public shouldShowGettingStartedLink(): ng.IPromise<boolean> {
    return this.$q.when(false); // Suppressing this as this GettingStarted is deprecated and will be removed (TFS 872022).
  }

  private showNotifications(params: IGetGettingStartedNotificationsParams): ng.IPromise<any> {
    return this.$q.resolve(); // Prevent the modal from even being created. (TFS 872022)
  }

  private getHostName(url: string): string {
    const urlParsingNode = document.createElement('a');
    urlParsingNode.setAttribute('href', url);
    return urlParsingNode.hostname;
  }

  private fetchNotificationsUrl(params: IGetGettingStartedNotificationsParams): ng.IPromise<string> {
    return this.ServerService.sendRequest(new GetGettingStartedNotificationsRequest(params))
      .then((result: IGetGettingStartedNotificationsResult) => result.contentUrl);
  }

  private updateUserPreference(scope: IGettingStartedNotificationsScope) {
    return this.ServerService.sendRequest(new SetSuppressGettingStartedNotification({
      suppressGettingStartedNotifications: !scope.dialog.showNotificationsOnLogIn
    }));
  }

  private getGettingStartedGuidePanelProps(scope: IGettingStartedNotificationsScope): GettingStartedGuide.Props {
    const closeModal = () => scope.modal.close('invite_others_cta');
    return {
      title: this.$translate.instant('gettingStarted_titleHi', { USER_NAME: this.ServerService.sessionUserDisplayName() }),
      guidePanels: this.guidePanelFactory.getPanels(closeModal),
      angularContext: this.angularContextFactory.create(scope),
    };
  }
}

Module.instance().service('GettingStartedNotifications', GettingStartedNotifications);
